import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, FadeIn, MaskedLines } from "@/components/Motion";
import { ARTICLES, readingMinutes } from "@/data/articles";

function ArticleCard({ article, featured = false }) {
  return (
    <Link
      to={`/insights/${article.slug}`}
      data-testid={`insight-card-${article.slug}`}
      className={`group grid gap-8 border-b border-navy/10 py-10 transition-colors duration-300 hover:bg-mist/60 ${
        featured ? "lg:grid-cols-2 lg:items-center lg:gap-14 lg:py-14" : "sm:grid-cols-[1fr_auto] sm:items-center"
      }`}
    >
      {featured && (
        <div className="relative overflow-hidden">
          <img
            src={article.image}
            alt={article.alt}
            className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            loading="lazy"
          />
        </div>
      )}
      <div>
        <p className="eyebrow flex flex-wrap items-center gap-x-4 gap-y-2">
          {article.category}
          <span aria-hidden="true" className="hidden h-1 w-1 rotate-45 bg-gold sm:inline-block" />
          <span className="font-normal normal-case tracking-normal text-charcoal/45">
            {article.date} · {readingMinutes(article)} min read
          </span>
        </p>
        <h3
          className={`mt-5 font-serif font-semibold leading-[1.15] text-navy transition-colors group-hover:text-charcoal ${
            featured ? "text-3xl sm:text-4xl" : "text-2xl sm:text-[1.7rem]"
          }`}
        >
          {article.title}
        </h3>
        <p className={`mt-4 max-w-xl text-[15px] leading-relaxed text-charcoal/65 ${featured ? "sm:text-base" : ""}`}>
          {article.excerpt}
        </p>
        <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-navy">
          Read Article
          <ArrowRight
            aria-hidden="true"
            className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1"
          />
        </span>
      </div>
      {!featured && (
        <ArrowUpRight
          aria-hidden="true"
          className="hidden h-6 w-6 text-navy/25 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-gold sm:block"
        />
      )}
    </Link>
  );
}

const PRIORITY_ORDER = [
  "accounting-erp-the-financial-heart-of-your-business-software",
  "the-value-a-business-software-reseller-provides",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Insights() {
  const [dynamic, setDynamic] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((res) => setDynamic(res.data?.articles || []))
      .catch(() => {});
  }, []);

  const prioritized = [
    ...PRIORITY_ORDER.map((s) => ARTICLES.find((a) => a.slug === s)).filter(Boolean),
    ...ARTICLES.filter((a) => !PRIORITY_ORDER.includes(a.slug)),
  ];
  const all = [...prioritized, ...dynamic];
  const [featured, ...rest] = all;
  return (
    <>
      <Seo
        title="Insights | Steven Philips & Associates Limited"
        description="Advisory perspectives from Steven Philips & Associates Limited on strategy, tax and financial discipline, operational efficiency, corporate governance, and human capital in Nigeria."
      />
      <section className="relative overflow-hidden bg-navy-deep pb-20 pt-40 lg:pb-28 lg:pt-52">
        <div className="grain absolute inset-0" aria-hidden="true" />
        <div
          aria-hidden="true"
          className="absolute -right-24 top-0 h-full w-1/2 bg-[radial-gradient(ellipse_at_top_right,rgba(196,164,106,0.12),transparent_60%)]"
        />
        <div className="container-x relative max-w-3xl">
          <FadeIn y={16}>
            <p className="eyebrow flex items-center gap-4">
              <span aria-hidden="true" className="h-px w-10 bg-gold" />
              Insights
            </p>
          </FadeIn>
          <h1 className="mt-7 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-6xl">
            <MaskedLines
              delay={0.3}
              lines={[
                <span key="i1">Perspectives on Strategy,</span>,
                <span key="i2">
                  Finance &amp; <em className="font-medium italic text-gold">Growth</em>
                </span>,
              ]}
            />
          </h1>
          <FadeIn delay={0.9}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Field notes from our advisory practice — considered perspectives for
              leaders navigating strategy, financial discipline, operations,
              governance, and people in the Nigerian and global market landscape.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="insights-page">
        <div className="container-x">
          <Reveal>
            <ArticleCard article={featured} featured />
          </Reveal>
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.05}>
              <ArticleCard article={a} />
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
